import { useState, useEffect, useRef, ReactNode, CSSProperties } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

export type SortDirection = "asc" | "desc" | null;

export interface Column<T> {
    key: keyof T | string;
    label: React.JSX.Element | string;
    width?: string;
    sortable?: boolean;
    align?: "left" | "center" | "right";
    render?: (value: unknown, row: T, index: number) => ReactNode;
    className?: string;
    styles?: CSSProperties;
}

export interface PaginationState {
    page: number;
    pageSize: number;
    total: number;
}

export interface SortState {
    key: string | null;
    direction: SortDirection;
}

export interface IDataTableDivider {
    content: React.JSX.Element | string;
    classes?: string;
    styles?: CSSProperties;
}
export type DataTableDividers = {[key: number]: IDataTableDivider};

export interface DataTableProps<T extends Record<string, unknown>> {
    columns: Column<T>[];
    data: T[];
    loading?: boolean;
    pagination: PaginationState;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onSortChange?: (sort: SortState) => void;
    onRowClick?: (row: T) => void;
    onRowDoubleClick?: (row: T) => void;
    pageSizeOptions?: number[];
    rowCss?: string | ((row: T, idx: number) => string);
    rowKey?: keyof T | ((row: T) => string);
    emptyMessage?: string;
    className?: string;
    dividers?: DataTableDividers;
    styles?: CSSProperties;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SortIcon({ direction }: { direction: SortDirection }) {
    return (
        <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, marginLeft: 6, verticalAlign: "middle" }}>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
                <path d="M4 0L8 5H0L4 0Z" fill={direction === "asc" ? "#fff" : "rgba(255,255,255,0.35)"} />
            </svg>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
                <path d="M4 5L0 0H8L4 5Z" fill={direction === "desc" ? "#fff" : "rgba(255,255,255,0.35)"} />
            </svg>
        </span>
    );
}

function Spinner() {
    return (
        <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10,
            background: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            borderRadius: "0 0 10px 10px",
            transition: "opacity 0.2s",
        }}>
            <div style={{
                width: 44, height: 44,
                borderRadius: "50%",
                border: "3px solid #e2e8f0",
                borderTopColor: "#4a5568",
                animation: "dt-spin 0.7s linear infinite",
            }} />
        </div>
    );
}

function PaginationButton({
    children, onClick, disabled, active,
}: { children: ReactNode; onClick: () => void; disabled?: boolean; active?: boolean }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            style={{
                minWidth: 32, height: 32, padding: "0 6px",
                borderRadius: 6,
                border: active ? "1.5px solid #4a5568" : "1.5px solid #e2e8f0",
                background: active ? "#4a5568" : disabled ? "#f7f8fa" : "#fff",
                color: active ? "#fff" : disabled ? "#b0b8c1" : "#4a5568",
                fontFamily: "inherit",
                fontSize: 13, fontWeight: active ? 600 : 400,
                cursor: disabled ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
                lineHeight: 1,
            }}
        >
            {children}
        </button>
    );
}

// ── Pagination helpers ───────────────────────────────────────────────────────

function buildPageRange(current: number, total: number): (number | "…")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | "…")[] = [1];
    if (current > 3) pages.push("…");
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) pages.push(p);
    if (current < total - 2) pages.push("…");
    pages.push(total);
    return pages;
}

// ── Main Component ───────────────────────────────────────────────────────────

export function DataTable<T extends Record<string, unknown>>({
    columns,
    data,
    loading = false,
    pagination,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onRowClick,
    onRowDoubleClick,
    pageSizeOptions = [10, 25, 50, 100],
    rowCss,
    rowKey,
    emptyMessage = "No data available",
    className = "",
    dividers = {},
    styles = {},
}: DataTableProps<T>) {
    const [sort, setSort] = useState<SortState>({ key: null, direction: null });
    const bodyRef = useRef<HTMLDivElement>(null);

    const { page, pageSize, total } = pagination;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, total);

    // Reset scroll on page change
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [page, pageSize]);

    function handleSort(col: Column<T>) {
        if (!col.sortable) return;
        const key = String(col.key);
        const next: SortState =
            sort.key !== key
                ? { key, direction: "asc" }
                : sort.direction === "asc"
                    ? { key, direction: "desc" }
                    : { key: null, direction: null };
        setSort(next);
        onSortChange?.(next);
    }

    function getRowClassNames(row: T, idx: number): string {
        var extraClasses = '';
        if (rowCss) {
            if (typeof rowCss === "function") {
                extraClasses = rowCss(row, idx);
            } else {
                extraClasses = rowCss;
            }
        };
        if (extraClasses.startsWith('!')) {
            return extraClasses.slice(1);
        }
        return `dt-row group ${extraClasses} ${onRowClick || onRowDoubleClick ? 'clickable' : ''}`;
    }

    function getRowKey(row: T, idx: number): string {
        if (!rowKey) return String(idx);
        if (typeof rowKey === "function") return rowKey(row);
        return String(row[rowKey] ?? idx);
    }

    const colWidths = columns.map((c) => c.width ?? "1fr");

    const gridTemplate = colWidths.join(" ");
    var tmr: NodeJS.Timeout | null = null;

    const tableStyles: CSSProperties = {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
        fontSize: 14,
        color: "#000",
        background: "#fff",
        border: "1.5px solid #dde2ea",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 2px 12px 0 rgba(74,85,104,0.08)",
        ...styles,
    };

    return (
        <>
            {/* Keyframe injection */}
            <style>{`
        @keyframes dt-spin { to { transform: rotate(360deg); } }
        @keyframes dt-fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .dt-row:hover .dt-cell { background: #f0f4f8 !important; }
        .dt-row { animation: dt-fadeIn 0.18s ease both; }
        .dt-sortable:hover { background: rgba(255,255,255,0.12) !important; cursor: pointer; }
        .dt-pg-btn:hover:not(:disabled) { background: #f0f4f8 !important; }
      `}</style>
            <div
                className={className}
                style={tableStyles}
            >
                {/* ── HEADER ───────────────────────────────────────────────────────── */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: gridTemplate,
                        background: "#4a5568",
                        color: "#fff",
                        flexShrink: 0,
                        borderBottom: "2px solid rgba(255,255,255,0.08)",
                    }}
                >
                    {columns.map((col) => (
                        <div
                            key={String(col.key)}
                            className={col.sortable ? "dt-sortable" : ""}
                            onClick={() => handleSort(col)}
                            style={{
                                padding: "13px 16px",
                                fontWeight: 600,
                                fontSize: 12,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                textAlign: col.align ?? "left",
                                userSelect: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                justifyContent:
                                    col.align === "right" ? "flex-end"
                                        : col.align === "center" ? "center"
                                            : "flex-start",
                                transition: "background 0.15s",
                                borderRadius: 0,
                            }}
                        >
                            {col.label}
                            {col.sortable && (
                                <SortIcon direction={sort.key === String(col.key) ? sort.direction : null} />
                            )}
                        </div>
                    ))}
                </div>

                {/* ── BODY (scrollable) ─────────────────────────────────────────────── */}
                <div
                    ref={bodyRef}
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        position: "relative",
                        minHeight: 0,
                        scrollbarWidth: "thin",
                        scrollbarColor: "#cbd5e0 transparent",
                    }}
                >
                    {loading && <Spinner />}

                    {!loading && data.length === 0 ? (
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            height: "100%", minHeight: 120,
                            color: "#9aa5b4", fontSize: 14, fontStyle: "italic",
                        }}>
                            {emptyMessage}
                        </div>
                    ) : (
                        data.map((row, rowIdx) => (<>
                            { dividers && dividers[rowIdx] && 
                                <div
                                    key={`DIV-${getRowKey(row, rowIdx)}`}
                                    className={dividers[rowIdx].classes ?? getRowClassNames(row, rowIdx)}
                                    style={dividers[rowIdx].styles ?? {
                                        display: "grid",
                                        gridTemplateColumns: gridTemplate,
                                        borderBottom: "1px solid #edf0f5",
                                        animationDelay: `${rowIdx * 0.03}s`,
                                    }}
                                >
                                    {dividers[rowIdx].content}
                                </div>
                            }
                            <div
                                key={getRowKey(row, rowIdx)}
                                className={getRowClassNames(row, rowIdx)}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: gridTemplate,
                                    borderBottom: "1px solid #edf0f5",
                                    animationDelay: `${rowIdx * 0.03}s`,
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (tmr) {
                                        clearTimeout(tmr);
                                    }
                                    if (e.detail > 1) {
                                        if (onRowDoubleClick) {
                                            onRowDoubleClick(row);
                                        }
                                    } else {
                                        if (onRowClick) {
                                            tmr = setTimeout(() => {
                                                onRowClick(row);
                                            }, 250);
                                        }
                                    }
                                }}
                            >
                                {columns.map((col) => {
                                    const raw = row[col.key as keyof T];
                                    return (
                                        <div
                                            key={String(col.key)}
                                            className={col.className ? (col.className.startsWith('!') ? col.className.slice(1) : `dt-cell ${col.className}`) : 'dt-cell'}
                                            style={{
                                                padding: "3px 16px",
                                                display: "flex",
                                                alignItems: "center",
                                                textAlign: col.align ?? "left",
                                                justifyContent:
                                                    col.align === "right" ? "flex-end"
                                                        : col.align === "center" ? "center"
                                                            : "flex-start",
                                                // background: rowIdx % 2 === 0 ? "#fff" : "#f9fafb",
                                                // transition: "background 0.12s",
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                color: "#000",
                                                ...col.styles
                                            }}
                                        >
                                            {col.render
                                                ? col.render(raw, row, rowIdx)
                                                : raw != null
                                                    ? String(raw)
                                                    : <span style={{ color: "#b0b8c1" }}>—</span>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        </>))
                    )}
                </div>

                {/* ── FOOTER (pagination) ───────────────────────────────────────────── */}
                <div
                    style={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 12,
                        padding: "10px 16px",
                        borderTop: "1.5px solid #edf0f5",
                        background: "#f9fafb",
                    }}
                >
                    {/* Left: row count + page size */}
                    {total !== -1 && <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                        <span style={{ color: "#6b7280", fontSize: 13 }}>
                            {total === 0
                                ? "No results"
                                : `Showing ${startItem}–${endItem} of ${total.toLocaleString()}`}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <label htmlFor="dt-page-size" style={{ fontSize: 13, color: "#6b7280" }}>
                                Rows:
                            </label>
                            <select
                                id="dt-page-size"
                                value={pageSize}
                                onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
                                style={{
                                    height: 32, padding: "0 28px 0 10px",
                                    border: "1.5px solid #e2e8f0", borderRadius: 6,
                                    background: "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%234a5568'/%3E%3C/svg%3E\") no-repeat right 8px center",
                                    appearance: "none", WebkitAppearance: "none",
                                    fontSize: 13, color: "#4a5568", fontFamily: "inherit",
                                    cursor: "pointer", outline: "none",
                                }}
                            >
                                {pageSizeOptions.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>}

                    {/* Right: page links */}
                    {total !== -1 && <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {/* Prev */}
                        <PaginationButton onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
                            <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
                                <path d="M6 1L1 5.5L6 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                        </PaginationButton>

                        {buildPageRange(page, totalPages).map((p, i) =>
                            p === "…" ? (
                                <span key={`ellipsis-${i}`} style={{ padding: "0 4px", color: "#b0b8c1", fontSize: 14 }}>…</span>
                            ) : (
                                <PaginationButton
                                    key={p}
                                    onClick={() => onPageChange(p)}
                                    active={p === page}
                                >
                                    {p}
                                </PaginationButton>
                            )
                        )}

                        {/* Next */}
                        <PaginationButton onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
                            <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
                                <path d="M1 1L6 5.5L1 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                        </PaginationButton>
                    </div>}
                </div>
            </div>
        </>
    );
}